import React, { useMemo, useState } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import { debug } from "../../../../../utils/debugLogger";
import { saveGscProperties } from "../../../../../services/socialTokenService";
import { GSCProperty } from "../types/gsc.types";
import PropertyCard from "./PropertyCard";

interface GSCPropertySelectionProps {
  properties: GSCProperty[];
  /** Customer the grant was started for; every property is connected to this one. */
  customerId: string;
  /** Google account that granted access, so a wrong login is obvious before saving. */
  email?: string;
  onClose: () => void;
}

const GSCPropertySelection: React.FC<GSCPropertySelectionProps> = ({
  properties,
  customerId,
  email,
  onClose,
}) => {
  const { idToken } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [connected, setConnected] = useState<Record<string, boolean>>({});
  const [savingSiteUrl, setSavingSiteUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filteredProperties = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return properties;
    return properties.filter((p) => p.site_url.toLowerCase().includes(term));
  }, [properties, searchTerm]);

  const handleConnect = async (property: GSCProperty) => {
    if (!idToken) {
      setError("Your session has expired. Please sign in again.");
      return;
    }
    setSavingSiteUrl(property.site_url);
    setError(null);
    try {
      await saveGscProperties(customerId, [property.site_url], idToken);
      // Local flip only: the Connect tab re-reads the customer on return, so there is
      // nothing to refetch here.
      setConnected((prev) => ({ ...prev, [property.site_url]: true }));
    } catch (err: any) {
      debug.error("Failed to connect Search Console property:", err);
      setError(err?.message || "Failed to connect the property. Please try again.");
    } finally {
      setSavingSiteUrl(null);
    }
  };

  return (
    <>
      <header className="mb-8 pb-8 border-b border-gray-100">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
          Connect Google Search Console
        </h1>
        <p className="text-sm text-gray-500">
          Choose the properties to connect
          {email ? (
            <>
              {" from "}
              <span className="font-medium text-gray-700">{email}</span>
            </>
          ) : null}
          .
        </p>
      </header>

      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">
          Available Search Console properties
        </h2>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search properties"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:ring-brand-primary focus:border-brand-primary text-sm"
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {filteredProperties.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">
          No properties found. Try a different search term.
        </p>
      ) : (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {filteredProperties.map((property) => (
            <PropertyCard
              key={property.site_url}
              property={property}
              onConnect={handleConnect}
              isConnected={!!connected[property.site_url]}
              isSaving={savingSiteUrl === property.site_url}
            />
          ))}
        </section>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg bg-gray-800 text-white font-medium hover:bg-gray-900"
        >
          Done
        </button>
      </div>
    </>
  );
};

export default GSCPropertySelection;
